import type { ConfigErrorEvent } from './events'
import type { ConfigChangeEvent, FileChangeEvent } from './events'
import { Events, type InitializeEvent } from './events'
import { loadConfigToState, type ServerState, updatePageState } from './state'
import type { Logger } from './types'
import { watchConfigFiles } from './watch'
import type { WebSocket } from '@fastify/websocket'
import type { FSWatcher } from 'chokidar'

/**
 * Sends formatted messages to the client when the config changes
 */
export function attachWatcherSocketEvents({
  watcher,
  socket,
  options,
  configPath,
  state,
  onMessage,
}: {
  watcher: FSWatcher
  socket: WebSocket
  options: any
  configPath: string
  state: ServerState
  onMessage: Logger
}) {
  socket.send(
    JSON.stringify({
      type: Events.Initialize,
      data: options,
    } satisfies InitializeEvent),
  )

  /** Setup the event emission for file changes */
  watchConfigFiles({
    configPath,
    watcher,
    logger: onMessage,
    onConfigChange: async (data) => {
      await loadConfigToState({
        project: data.project,
        documents: data.documents,
        references: data.references,
        state,
      })

      socket.send(
        JSON.stringify({
          type: Events.ConfigChange,
          data: {
            project: data.project,
            theme: data.theme,
          },
        } satisfies ConfigChangeEvent),
      )
    },
    onConfigError: (errors) => {
      socket.send(
        JSON.stringify({
          type: Events.ConfigError,
          data: { errors },
        } satisfies ConfigErrorEvent),
      )
    },
    onFileChange: async ({ uid, content, meta }) => {
      const reference = state.references[uid]
      const page = state.pages[uid]
      const pageContent = state.pageContent[uid]

      if (reference) {
        state.references[uid].content = content
      } else if (page && pageContent) {
        await updatePageState({
          state,
          uid,
          content,
          meta,
        })
      } else {
        return console.error('Unknown file change', uid)
      }

      socket.send(
        JSON.stringify({
          type: Events.FileChange,
          data: reference
            ? { uid, versionUid: reference.versionUid }
            : {
                uid,
                guideUid: page.guideUid,
                versionUid: page.versionUid,
                page: page.page,
              },
        } satisfies FileChangeEvent),
      )
    },
  })
}
